import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ParamMap } from '@angular/router';
import { environment } from '../../../environments/environment';

export type AcquisitionCodeType = 'referral' | 'affiliate';

export interface StoredAcquisitionCode {
  type: AcquisitionCodeType;
  code: string;
}

/**
 * Persists referral (`ref`) or affiliate (`aff` / `affiliate`) codes from the URL
 * so they survive navigation to signup/login and are sent once per channel on auth.
 */
@Injectable({
  providedIn: 'root',
})
export class AcquisitionCodeService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly storageKey = `${environment.channelCode}:acquisition-code`;

  captureFromParamMap(params: ParamMap): void {
    this.captureFromValues({
      ref: params.get('ref'),
      aff: params.get('aff'),
      affiliate: params.get('affiliate'),
    });
  }

  captureFromQueryParams(queryParams: Record<string, string | string[] | undefined>): void {
    this.captureFromValues({
      ref: this.firstString(queryParams['ref']),
      aff: this.firstString(queryParams['aff']),
      affiliate: this.firstString(queryParams['affiliate']),
    });
  }

  getStored(): StoredAcquisitionCode | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    const raw = sessionStorage.getItem(this.storageKey);
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as StoredAcquisitionCode;
      if (
        (parsed.type === 'referral' || parsed.type === 'affiliate') &&
        typeof parsed.code === 'string' &&
        parsed.code.trim().length > 0
      ) {
        return { type: parsed.type, code: parsed.code.trim() };
      }
    } catch {
      sessionStorage.removeItem(this.storageKey);
    }

    return null;
  }

  /** Query string suffix for Auth/signup and Auth/login (e.g. `?ref=ABC` or `?aff=XYZ`). */
  buildAuthQueryString(): string {
    const stored = this.getStored();
    if (!stored) {
      return '';
    }

    const param = stored.type === 'affiliate' ? 'aff' : 'ref';
    return `?${param}=${encodeURIComponent(stored.code)}`;
  }

  /**
   * Query params to keep on login ↔ signup ↔ forgot-password links
   * (`ref` / `aff` plus optional `returnUrl`).
   */
  buildAuthNavigationQueryParams(returnUrl?: string | null): Record<string, string> {
    const params: Record<string, string> = {};
    const stored = this.getStored();
    if (stored) {
      params[stored.type === 'affiliate' ? 'aff' : 'ref'] = stored.code;
    }

    const trimmedReturn = returnUrl?.trim();
    if (trimmedReturn) {
      params['returnUrl'] = trimmedReturn;
    }

    return params;
  }

  clear(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    sessionStorage.removeItem(this.storageKey);
  }

  private captureFromValues(values: {
    ref: string | null | undefined;
    aff: string | null | undefined;
    affiliate: string | null | undefined;
  }): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const affiliateCode = values.aff?.trim() || values.affiliate?.trim();
    const referralCode = values.ref?.trim();

    if (affiliateCode) {
      this.save({ type: 'affiliate', code: affiliateCode });
      return;
    }

    if (referralCode) {
      this.save({ type: 'referral', code: referralCode });
    }
  }

  private save(code: StoredAcquisitionCode): void {
    sessionStorage.setItem(this.storageKey, JSON.stringify(code));
  }

  private firstString(value: string | string[] | undefined): string | undefined {
    if (Array.isArray(value)) {
      return value[0];
    }
    return value;
  }
}
